import React, { type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Card } from '@/components/ui/card';
import type { SelectOption } from '@/components/ui/select';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import { parseObjectToSearchParams } from '@/utils/parseSearchParams';

import type { EstatesFiltersSchemaType } from '../schemas';
import { DEFAULT_ESTATE_FILTERS_VALUES } from '../schemas/estates-filters';
import { EstateFilterAdditionalFeatures } from './estate-filter-additional-features';
import { EstateFilterDates } from './estate-filter-dates';
import { EstateFilterLivingAreaRooms } from './estate-filter-living-area-rooms';
import { EstateFilterPrices } from './estate-filter-prices';
import { EstateFilterProject } from './estate-filter-project';
import { EstateFilterPropertyDetails } from './estate-filter-property-details';

export interface EstateFilterProps {
  cardClassName?: string;
  gridClassName?: string;
  creatorList?: SelectOption[];
  searchParams: {
    enableFilters?: boolean;
    filters?: EstatesFiltersSchemaType;
    filterOwn?: boolean;
    view?: 'map' | 'grid' | 'list';
  };
  viewOptions?: {
    hideLivingAreaAndRooms?: boolean;
  };
  onUpdateSearchParams: (filters: EstatesFiltersSchemaType) => void;
}

export const EstateFilter: FC<EstateFilterProps> = ({
  creatorList,
  searchParams,
  cardClassName,
  onUpdateSearchParams,
}) => {
  const { enableFilters, filters, filterOwn } = searchParams;

  const methods = useForm<EstatesFiltersSchemaType>({
    defaultValues: {
      ...DEFAULT_ESTATE_FILTERS_VALUES,
      creatorId: filterOwn && creatorList?.length ? creatorList[0].id : undefined,
    },
  });

  const { reset, watch } = methods;

  const updateSearchParams = (params: EstatesFiltersSchemaType) => {
    const filters = parseObjectToSearchParams<EstatesFiltersSchemaType>(params);
    onUpdateSearchParams(filters);
  };

  const debouncedUpdateSearchParamFn = useDebouncedCallback(updateSearchParams, 1000);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useEffect(() => {
    reset((prev) => ({ ...prev, ...filters }));
    const sub = watch((data) => {
      debouncedUpdateSearchParamFn(data);
    });

    return () => sub.unsubscribe();
  }, []);

  return (
    <FormProvider {...methods}>
      <Card className={cn('rounded-md p-3 shadow-none', cardClassName)}>
        <div
          className={cn(
            'transition-grid-template-rows grid duration-200 motion-reduce:transition-none',
            enableFilters ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className={cn('overflow-hidden')}>
            <EstateFilterPrices />
            <fieldset className="mb-6 grid grid-cols-1 gap-6">
              <EstateFilterLivingAreaRooms />
            </fieldset>
            <EstateFilterProject />
            <EstateFilterPropertyDetails searchParams={searchParams} creatorList={creatorList} />
            <EstateFilterDates />
            <EstateFilterAdditionalFeatures />
          </div>
        </div>
      </Card>
    </FormProvider>
  );
};
